import React, { useEffect, useMemo, useState } from "react"
import _ from "lodash"
import { useDispatch } from "react-redux"
import { Message, toaster } from "rsuite"
import { Button, InputAdornment, LinearProgress, TextField } from "@mui/material"
import {
    Add,
    Download,
    GroupsOutlined,
    ImportContacts,
    RefreshOutlined,
    SchoolOutlined,
    Search,
    SearchOff
} from "@mui/icons-material"

// Still imported: other pages share some of its global class names.
import "./classStudents.css"

import { userAllowingOperations, GetCurrentUser, generateRandomId } from "../utils/hooks"
import { Loader, ConfirmationAlert } from "../components/index"
import { useSnackbar } from "../components/Snakebar"
import EmptyState from "../Lectures/components/EmptyState"
import ClassList from "../components/ClassList"
import StudentList from "./classroom/StudentList"
import {
    matchesSearch,
    saveBlob,
    sortByRollNo,
    toDownloadRows,
    toFormFields,
    todayString
} from "./classroom/helpers"
import ManageFee from "./components/Fee"
import AddStudentModal from "./components/AddStudent"
import ImportStudentsPopover from "./components/BulkStudentImport"

import { updateFields, resetForm } from "../Redux/actions/formValue.action"
import { useLazyGetInstitutionClassesQuery } from "../Redux/actions/classSetup.action"
import {
    useDownloadAdmissionDocumentMutation,
    useDownloadAdmissionRecievedMutation,
    useDownloadExcelFileMutation
} from "../Redux/actions/download.action"
import { useLazyGetUserInstitutionQuery } from "../Redux/actions/setting.action"
import {
    useLazyGetStudentPaidDataQuery,
    useCollectFeeMutation
} from "../Redux/actions/feeCollection.action"
import {
    useLazyGetClassStudentsQuery,
    useLazyDownloadStudentBulkImportTemplateQuery,
    useDeleteStudentMutation
} from "../Redux/actions/studentAction"

const toast = (type, message) =>
    toaster.push(<Message type={type}>{message}</Message>, { placement: "topCenter" })

const pillButtonSx = { textTransform: "none", borderRadius: "10px", whiteSpace: "nowrap" }

export default function ClassStudents() {
    const dispatch = useDispatch()
    const { showSnackbar } = useSnackbar()
    const privileges = useMemo(() => userAllowingOperations(), [])
    const schoolId = useMemo(() => _.get(GetCurrentUser(), "meta_data.user_platform", null), [])

    const [activeClassId, setActiveClassId] = useState(null)
    const [search, setSearch] = useState("")
    const [receiptBusyId, setReceiptBusyId] = useState(null)
    const [feeStudent, setFeeStudent] = useState(null)
    const [collecting, setCollecting] = useState(false)
    const [studentModal, setStudentModal] = useState({ open: false, student: null })
    const [importOpen, setImportOpen] = useState(false)
    const [deleteTarget, setDeleteTarget] = useState(null)

    const [getClasses, classesState] = useLazyGetInstitutionClassesQuery()
    const [getClassStudents, studentsState] = useLazyGetClassStudentsQuery()
    const [getUserSchool, schoolState] = useLazyGetUserInstitutionQuery()
    const [getPaidData, paidDataState] = useLazyGetStudentPaidDataQuery()
    const [collectFee] = useCollectFeeMutation()
    const [downloadAdmissionReceipt] = useDownloadAdmissionRecievedMutation()
    const [downloadFeeReceiptApi, feeReceiptState] = useDownloadAdmissionDocumentMutation()
    const [downloadStudentsApi, downloadState] = useDownloadExcelFileMutation()
    const [downloadTemplateApi, templateState] = useLazyDownloadStudentBulkImportTemplateQuery()
    const [deleteStudentApi, deleteState] = useDeleteStudentMutation()

    /* ------------------------------ data ------------------------------ */

    const classes = useMemo(() => {
        const list = _.get(classesState, "data.list", [])
        return Array.isArray(list) ? list : []
    }, [classesState])

    const activeClass = useMemo(
        () => classes.find((item) => item.id === activeClassId) || null,
        [classes, activeClassId]
    )

    const students = useMemo(() => {
        if (!activeClass) return []
        const list = _.get(studentsState, "currentData.list", [])
        return Array.isArray(list) ? sortByRollNo(list) : []
    }, [activeClass, studentsState])

    const visibleStudents = useMemo(
        () => students.filter((student) => matchesSearch(student, search)),
        [students, search]
    )

    const school = useMemo(() => {
        if (!schoolState.isSuccess) return null
        const info = _.get(schoolState, "currentData.data", {})
        return {
            school_name: _.get(info, "name", ""),
            school_address: `${_.get(info, "state", "")} ${_.get(info, "district", "")} ${_.get(info, "pin", "")} ${_.get(info, "address", "")}`
        }
    }, [schoolState])

    const paidFees = useMemo(
        () => (paidDataState.isSuccess ? _.get(paidDataState, "currentData.data", []) : []),
        [paidDataState]
    )

    /* ----------------------------- fetching ---------------------------- */

    const fetchClasses = () => {
        if (schoolId) getClasses({ institution_ref: schoolId })
    }

    const fetchStudents = (item = activeClass) => {
        if (!item?.id || !item?.institution_ref) return
        getClassStudents({ class_id: item.id, school_id: item.institution_ref })
    }

    useEffect(() => {
        if (!schoolId) return
        fetchClasses()
        getUserSchool(schoolId)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const selectClass = (item) => {
        setActiveClassId(item.id)
        setSearch("")
        fetchStudents(item)
    }

    // Open the first class once classes arrive, or if the open one disappears
    useEffect(() => {
        if (classes.length > 0 && !activeClass) selectClass(classes[0])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes])

    const refresh = () => {
        fetchClasses()
        fetchStudents()
    }

    /* --------------------------- permissions --------------------------- */

    const allowed = (privilege, message, severity = "warning") => {
        if (privileges[privilege]) return true
        showSnackbar({ message, severity })
        return false
    }

    /* ------------------------------ toolbar ----------------------------- */

    const openAddStudent = () => {
        if (!allowed("add_member", "Not Allow to add student!, Please connect with Admin")) return
        // Start from an empty form, not whatever the last edit left behind
        dispatch(resetForm())
        setStudentModal({ open: true, student: null })
    }

    const openImport = () => {
        if (!allowed("upload", "Not Allow to upload the content!, Please connect with Admin", "info")) return
        setImportOpen(true)
    }

    const downloadStudents = async () => {
        if (visibleStudents.length === 0) return
        if (!allowed("generate", "Not Allow to download student list!, Please connect with Admin")) return
        try {
            const blob = await downloadStudentsApi(toDownloadRows(visibleStudents)).unwrap()
            saveBlob(blob, activeClass ? `${activeClass.class_name}-students.xlsx` : "bulk_student.xlsx")
        } catch {
            showSnackbar({ message: "Couldn't download the student list. Please try again.", severity: "error" })
        }
    }

    const downloadTemplate = async () => {
        try {
            const blob = await downloadTemplateApi().unwrap()
            saveBlob(blob, "bulk_student.xlsx")
        } catch {
            showSnackbar({
                message: "Error while downloading the template, Please try after sometime.",
                severity: "error"
            })
        }
    }

    /* ---------------------------- row actions --------------------------- */

    const downloadReceipt = async (student) => {
        if (!allowed("generate", "Not Allow to download the admission receipt!, Please connect with Admin")) return
        if (!school || !activeClass) {
            showSnackbar({ message: "School details are still loading. Please try again.", severity: "info" })
            return
        }

        setReceiptBusyId(student.id)
        try {
            const blob = await downloadAdmissionReceipt({
                ...school,
                student_name: student.name,
                class_name: activeClass.class_name,
                application_no: generateRandomId(),
                submitted_date: `${new Date().getDate()} /${new Date().getMonth() + 1} /${new Date().getFullYear()}`,
                review_text: "Application completed successfully!"
            }).unwrap()
            saveBlob(blob, "admission-receipt.pdf")
        } catch {
            showSnackbar({ message: "Couldn't download the admission receipt. Please try again.", severity: "error" })
        } finally {
            setReceiptBusyId(null)
        }
    }

    const openFee = (student) => {
        if (!allowed("generate", "Not Allow to download the Fee receipt!, Please connect with Admin")) return
        setFeeStudent(student)
        getPaidData({ school_id: student.school_ref, student_id: student.id })
    }

    const handleUpdatePayment = async ({ collections = [] }) => {
        if (collections.length === 0 || !feeStudent) return

        const payment_date = todayString()
        setCollecting(true)
        const results = await Promise.allSettled(
            collections.map((fee) =>
                collectFee({
                    school_ref: feeStudent.school_ref,
                    student_ref: feeStudent.id,
                    fee_name: fee.feeName,
                    fee_amount: fee.feeAmount,
                    payment_date
                }).unwrap()
            )
        )
        setCollecting(false)

        const saved = results.find((result) => result.status === "fulfilled")
        if (saved) toast("success", _.get(saved, "value.message", "Fee updated successfully!"))
        if (results.some((result) => result.status === "rejected")) {
            toast("error", "Error while updating the fee amount")
        }
        setFeeStudent(null)
    }

    const downloadFeeReceipt = async (paidData) => {
        if (!paidData || !school || !feeStudent || !activeClass) return
        try {
            const blob = await downloadFeeReceiptApi({
                school_name: school.school_name,
                school_address: school.school_address,
                student_name: feeStudent.name,
                class_name: activeClass.class_name,
                roll_no: String(feeStudent.roll_no),
                fee_items: paidData.map((fee) => ({
                    feeName: fee.fee_name,
                    feeAmount: String(fee.fee_amount)
                })),
                payment_date: todayString(),
                receipt_no: generateRandomId(12)
            }).unwrap()
            saveBlob(blob, "admission-fee-receipt.pdf")
        } catch {
            showSnackbar({ message: "Couldn't download the fee receipt. Please try again.", severity: "error" })
        }
    }

    const openEdit = (student) => {
        if (!allowed("edit_member", "Not Allow to edit the student bio-data, Please connect with Admin")) return
        dispatch(updateFields(toFormFields(student)))
        setStudentModal({ open: true, student })
    }

    const closeStudentModal = () => {
        setStudentModal({ open: false, student: null })
        fetchStudents()
    }

    const askDelete = (student) => {
        if (!allowed("delete_member", "Not Allow to delete student!, Please connect with Admin")) return
        setDeleteTarget(student)
    }

    const confirmDelete = async () => {
        const student = deleteTarget
        if (!student) return
        try {
            const result = await deleteStudentApi({
                school_ref: student.school_ref,
                class_ref: student.class_ref,
                student_ref: student.id
            }).unwrap()
            showSnackbar({ message: _.get(result, "message", "Student deleted."), severity: "success" })
            setDeleteTarget(null)
            fetchStudents()
        } catch (error) {
            showSnackbar({
                message: _.get(error, "data.detail", "Error while deleting the student."),
                severity: "error"
            })
        }
    }

    /* ------------------------------- view ------------------------------- */

    const loadingClasses = classesState.isLoading || classesState.isFetching
    const loadingStudents = studentsState.isFetching
    const blocking =
        classesState.isLoading ||
        paidDataState.isFetching ||
        collecting ||
        feeReceiptState.isLoading ||
        templateState.isFetching ||
        deleteState.isLoading

    const withDob = students.filter((student) => student?.dob).length
    const withEmail = students.filter((student) => student?.email).length

    const renderStudents = () => {
        if (studentsState.isError && !loadingStudents) {
            return (
                <div className="p-4 sm:p-6">
                    <EmptyState
                        icon={<GroupsOutlined />}
                        title="Couldn't load students"
                        description="Please check your connection and try again."
                        actionLabel="Retry"
                        onAction={() => fetchStudents()}
                    />
                </div>
            )
        }

        if (loadingStudents && students.length === 0) {
            return (
                <div className="space-y-3 p-5">
                    {[0, 1, 2, 3, 4].map((key) => (
                        <div key={key} className="h-12 animate-pulse rounded-lg bg-slate-100" />
                    ))}
                </div>
            )
        }

        if (students.length === 0) {
            return (
                <div className="p-4 sm:p-6">
                    <EmptyState
                        icon={<GroupsOutlined />}
                        title="No students yet"
                        description="Add a student or import students in bulk to this class."
                        actionLabel={privileges.add_member ? "Add student" : undefined}
                        onAction={privileges.add_member ? openAddStudent : undefined}
                    />
                </div>
            )
        }

        if (visibleStudents.length === 0) {
            return (
                <div className="p-4 sm:p-6">
                    <EmptyState
                        icon={<SearchOff />}
                        title="No students match"
                        description="Try a different name, roll number or email."
                        actionLabel="Clear search"
                        onAction={() => setSearch("")}
                    />
                </div>
            )
        }

        return (
            <StudentList
                students={visibleStudents}
                busyId={receiptBusyId}
                onReceipt={downloadReceipt}
                onFee={openFee}
                onEdit={openEdit}
                onDelete={askDelete}
            />
        )
    }

    return (
        <div className="mx-auto w-full max-w-[1400px] px-4 pb-24 pt-6 sm:px-6 lg:px-8">

            {/* Page header */}
            <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">
                        Student management
                    </p>
                    <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[28px]">
                        Class Students
                    </h1>
                    <p className="mt-1 max-w-xl text-sm text-slate-500">
                        Manage enrolled students, admission receipts, fee payments and student records.
                    </p>
                </div>
                <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<RefreshOutlined />}
                    onClick={refresh}
                    disabled={loadingClasses || loadingStudents}
                    sx={{ ...pillButtonSx, borderColor: "#e2e8f0", color: "#334155" }}
                >
                    Refresh
                </Button>
            </header>

            {!loadingClasses && classes.length === 0 ? (
                <EmptyState
                    icon={<SchoolOutlined />}
                    title={classesState.isError ? "Couldn't load classes" : "No classes found"}
                    description={
                        classesState.isError
                            ? "Please check your connection and try again."
                            : "Create a class first to start managing students."
                    }
                    actionLabel={classesState.isError ? "Retry" : undefined}
                    onAction={classesState.isError ? fetchClasses : undefined}
                />
            ) : (
                <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">

                    {/* Classes */}
                    <aside className="lg:sticky lg:top-4 lg:self-start">
                        <div className="mb-3 flex items-center justify-between">
                            <h2 className="text-sm font-semibold text-slate-700">Classes</h2>
                            <span className="text-xs tabular-nums text-slate-400">{classes.length}</span>
                        </div>
                        <ClassList
                            classes={classes}
                            activeId={activeClassId}
                            onSelect={selectClass}
                            loading={loadingClasses}
                        />
                    </aside>

                    {/* Students of the selected class */}
                    <section className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="h-1">
                            {loadingStudents && <LinearProgress sx={{ height: 3 }} />}
                        </div>

                        <div className="flex flex-wrap items-start justify-between gap-3 px-4 pt-4 sm:px-5">
                            <div className="min-w-0">
                                <h2 className="truncate text-lg font-semibold text-slate-900">
                                    {activeClass?.class_name || "Select a class"}
                                </h2>
                                <p className="text-sm text-slate-500">
                                    {students.length} student{students.length === 1 ? "" : "s"}
                                    {students.length > 0 && (
                                        <span className="text-slate-400">
                                            {" "}· {withDob} with DOB · {withEmail} with email
                                        </span>
                                    )}
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    variant="contained"
                                    disableElevation
                                    startIcon={<Add />}
                                    onClick={openAddStudent}
                                    disabled={!privileges.add_member || !activeClass}
                                    sx={pillButtonSx}
                                >
                                    Add student
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<ImportContacts />}
                                    onClick={openImport}
                                    disabled={!privileges.upload || !activeClass}
                                    sx={pillButtonSx}
                                >
                                    Bulk import
                                </Button>
                            </div>
                        </div>

                        {/* Search and download */}
                        <div className="mt-4 flex flex-col gap-3 border-y border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:px-5">
                            <TextField
                                size="small"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name, roll number or email"
                                inputProps={{ "aria-label": "Search students" }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                            />
                            <Button
                                variant="outlined"
                                color="inherit"
                                startIcon={<Download />}
                                onClick={downloadStudents}
                                disabled={
                                    !privileges.generate ||
                                    downloadState.isLoading ||
                                    visibleStudents.length === 0
                                }
                                sx={{ ...pillButtonSx, borderColor: "#e2e8f0", color: "#334155" }}
                            >
                                {downloadState.isLoading
                                    ? "Preparing…"
                                    : search.trim()
                                        ? `Download ${visibleStudents.length}`
                                        : "Download list"}
                            </Button>
                        </div>

                        {renderStudents()}
                    </section>
                </div>
            )}

            <ManageFee
                isOpen={Boolean(feeStudent && activeClass)}
                onClose={() => setFeeStudent(null)}
                data={activeClass}
                handleUpdatePayment={handleUpdatePayment}
                studentPaidData={paidFees}
                downloadFeeReceipt={downloadFeeReceipt}
            />

            <AddStudentModal
                openAddStudentModal={studentModal.open}
                handleCloseAddSudent={closeStudentModal}
                selectedClass={activeClass}
                isEdit={Boolean(studentModal.student)}
                selectedStudent={studentModal.student}
            />

            <ImportStudentsPopover
                open={importOpen}
                onClose={() => {
                    setImportOpen(false)
                    fetchStudents()
                }}
                onDownloadTemplate={downloadTemplate}
                selectedClass={activeClass}
            />

            <ConfirmationAlert
                open={Boolean(deleteTarget)}
                title="Delete student?"
                message={`${deleteTarget?.name || "This student"} will be removed from ${activeClass?.class_name || "the class"}. This can't be undone.`}
                onCancel={() => setDeleteTarget(null)}
                onConfirm={confirmDelete}
            />

            {blocking && <Loader show={true} />}
        </div>
    )
}
